'use client';

// «Tether Chand» app banner — the complement of the WordPress banner (that one sits at the top on desktop
// and at the bottom on phones; this one does the opposite):
// - phones/tablets (< lg): a sticky strip under the header. It sits in the page flow, so at the top of a
//   page it has its own space and covers nothing. Once stuck it slides up behind the header while scrolling
//   down and comes back on scroll up or after ~3 s idle — except while a page's own top bar is on screen
//   (coin page bar, platform tabs…): then it stays tucked away.
// - desktop (lg+): floating bar at the bottom; slides in after load, out while scrolling down, back on
//   scroll up or idle; stays out near the footer, while the cookie banner is open and above a page's own
//   bottom bar (`data-banner-bottom-anchor`).
// Both: out while typing in a field, while the app drawer is open and while a section showing the same
// content is on screen (`data-banner-hide`, the home page's Tether Chand showcase). «مشاهده» opens the drawer.
// Closing hides it for this page only: it is back on the next page or after a refresh.
import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { TetherCoin3D } from '@/components/Icons';
import { useAppSidebarStore } from '@/stores/layout/appSidebarStore';

const FIRST_SHOW_MS = 1800;
const IDLE_SHOW_MS = 3000;
const RETRY_MS = 800; // re-check while something blocks it
const DESKTOP_GAP = 12; // px between the desktop bar and the screen bottom (or a bottom page bar)
const SCROLL_DELTA = 6; // px; ignores tiny jitters (trackpads, iOS bounce)
const FOOTER_ZONE = 220; // px from the page bottom where the desktop bar stays out
const HEADER_H = 48; // the mobile header (h-12)
const DESKTOP_QUERY = '(min-width: 1024px)';

/** Bottom offset of the desktop bar, so it floats above a page's own bottom bar. */
const measureBottom = (): number => {
    let bottom = DESKTOP_GAP;
    // Pages may render a bar more than once (e.g. per breakpoint): only visible copies count.
    document.querySelectorAll('[data-banner-bottom-anchor]').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (
            r.width > 0 &&
            r.height > 0 &&
            r.bottom >= window.innerHeight - 2 &&
            r.top < window.innerHeight
        )
            bottom = Math.max(bottom, Math.round(window.innerHeight - r.top + DESKTOP_GAP));
    });
    return bottom;
};

/**
 * Wide sticky/fixed bars of the page itself (coin page bar, platform tabs…). Found once per page, so no
 * page has to be tagged; checked on every scroll by position only.
 */
const findPageBars = (banner: HTMLElement | null): HTMLElement[] => {
    const minWidth = window.innerWidth * 0.6;
    return Array.from(document.body.querySelectorAll<HTMLElement>('body *')).filter((el) => {
        if (el.closest('header') || (banner && banner.contains(el)) || el.closest('[role=dialog]'))
            return false;
        const { position } = getComputedStyle(el);
        return (
            (position === 'sticky' || position === 'fixed') &&
            el.getBoundingClientRect().width >= minWidth
        );
    });
};

/** A page bar sits right under the header right now. */
const pageBarOnScreen = (bars: HTMLElement[]) =>
    bars.some((el) => {
        const r = el.getBoundingClientRect();
        return r.height > 0 && r.height < 200 && r.top >= HEADER_H - 8 && r.top <= HEADER_H + 90;
    });

/**
 * A section that shows the same thing as the banner (e.g. the home page's Tether Chand showcase,
 * marked `data-banner-hide`) is on screen: the banner would only repeat it.
 */
const sameContentOnScreen = () =>
    Array.from(document.querySelectorAll('[data-banner-hide]')).some((el) => {
        const r = el.getBoundingClientRect();
        return (
            r.height > 0 &&
            r.top < window.innerHeight * 0.85 &&
            r.bottom > window.innerHeight * 0.15
        );
    });

const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;

const isTyping = () => {
    const el = document.activeElement as HTMLElement | null;
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
};

export default function TetherAppBanner() {
    const pathname = usePathname();
    const openSidebar = useAppSidebarStore((s) => s.openSidebar);
    const prefetchSidebarData = useAppSidebarStore((s) => s.prefetchSidebarData);
    const drawerOpen = useAppSidebarStore((s) => s.isOpen);
    const ref = useRef<HTMLElement>(null);

    const [closed, setClosed] = useState(false);
    // Desktop bar: starts out of view and slides in. Phone strip: starts in place (it is part of the page).
    const [desktopShown, setDesktopShown] = useState(false);
    const [mobileTucked, setMobileTucked] = useState(false);
    const [bottom, setBottom] = useState(DESKTOP_GAP);
    const [desktop, setDesktop] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(DESKTOP_QUERY);
        const update = () => setDesktop(media.matches);
        update();
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);

    // Set up per page: a new page (or a refresh) brings a closed banner back.
    useEffect(() => {
        setClosed(false);
        setDesktopShown(false);
        setMobileTucked(false);
        let desktopVisible = false;
        let tucked = false;

        let lastY = window.scrollY;
        let ready = false;
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        let frame = 0;
        let retrying = false;
        // Rescanned every few seconds, so bars that mount later (after data loads) are found too
        let bars: HTMLElement[] | null = null;
        let barsAt = 0;
        const pageBars = () => {
            if (!bars || Date.now() - barsAt > 5000) {
                bars = findPageBars(ref.current);
                barsAt = Date.now();
            }
            return bars;
        };

        /** The strip's place in the page: while the page is above it, it is simply in the flow. */
        const atTop = () => window.scrollY < 8;

        const blocked = () => {
            if (isTyping() || sameContentOnScreen()) return true;
            if (isDesktop())
                return (
                    !!document.querySelector('[data-cookie-banner]') ||
                    window.innerHeight + window.scrollY >=
                        document.documentElement.scrollHeight - FOOTER_ZONE
                );
            return pageBarOnScreen(pageBars());
        };

        const showWhenIdle = () => {
            if (retrying) return; // a quicker re-check is already scheduled
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => set(true), IDLE_SHOW_MS);
        };

        const updatePlace = () => {
            if (isDesktop()) setBottom(measureBottom());
        };

        const set = (wanted: boolean) => {
            updatePlace();
            const isBlocked = wanted && blocked();
            if (isDesktop()) {
                const next = wanted && ready && !isBlocked;
                if (next !== desktopVisible) {
                    desktopVisible = next;
                    setDesktopShown(next);
                }
            } else {
                // At the very top the strip is part of the page and never tucks away (no empty gap).
                const next = atTop() ? false : !wanted || isBlocked;
                if (next !== tucked) {
                    tucked = next;
                    setMobileTucked(next);
                }
            }
            // Wanted but blocked (a page bar, typing, the footer…): check again shortly.
            if (isBlocked && (ready || !isDesktop())) {
                retrying = true;
                clearTimeout(idleTimer);
                idleTimer = setTimeout(() => {
                    retrying = false;
                    set(true);
                }, RETRY_MS);
            }
        };

        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                const y = window.scrollY;
                const delta = y - lastY;
                if (atTop()) {
                    set(true);
                    lastY = y;
                } else if (Math.abs(delta) >= SCROLL_DELTA) {
                    if (delta > 0) {
                        // Scrolling down always hides, and cancels a pending re-check.
                        retrying = false;
                        set(false);
                    } else {
                        set(true);
                    }
                    lastY = y;
                }
                showWhenIdle();
            });
        };
        // Only fields hide it (the keyboard); focusing the banner's own buttons must not.
        const onFocus = () => {
            if (isTyping()) set(false);
        };
        const onResize = () => {
            bars = null;
            updatePlace();
        };

        const firstShow = setTimeout(() => {
            ready = true;
            set(true);
        }, FIRST_SHOW_MS);

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        document.addEventListener('focusin', onFocus);
        document.addEventListener('focusout', showWhenIdle);
        return () => {
            clearTimeout(firstShow);
            clearTimeout(idleTimer);
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('focusin', onFocus);
            document.removeEventListener('focusout', showWhenIdle);
        };
    }, [pathname]);

    if (closed) return null;

    const hidden = drawerOpen || (desktop ? !desktopShown : mobileTucked);

    return (
        <aside
            ref={ref}
            aria-label='اپلیکیشن تتر چند'
            aria-hidden={hidden}
            inert={hidden}
            style={
                {
                    '--tcb-bottom': `${bottom}px`,
                    // Inline so no utility can override it: quick start, soft landing.
                    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                } as React.CSSProperties
            }
            className={`sticky top-12 z-40 transition-[transform,opacity] duration-500 will-change-transform motion-reduce:transition-opacity lg:fixed lg:inset-x-3 lg:top-auto lg:bottom-[max(var(--tcb-bottom),env(safe-area-inset-bottom))] lg:mx-auto lg:max-w-[640px] ${
                hidden
                    ? 'pointer-events-none -translate-y-full motion-reduce:translate-y-0 motion-reduce:opacity-0 lg:translate-y-[calc(100%+var(--tcb-bottom)+0.5rem)]'
                    : 'translate-y-0'
            }`}
        >
            <div className='relative flex items-center overflow-hidden border-b border-white/10 bg-gradient-to-l from-[#01001B] via-[#0b0f3a] to-[#0a2a33] text-white shadow-[0_10px_24px_-14px_rgba(1,0,27,0.7)] lg:rounded-2xl lg:border-0 lg:shadow-[0_18px_40px_-12px_rgba(1,0,27,0.55)] lg:ring-1 lg:ring-white/10'>
                {/* Soft mint glow behind the coin */}
                <span
                    aria-hidden='true'
                    className='pointer-events-none absolute -right-6 top-1/2 size-20 -translate-y-1/2 rounded-full bg-[#42BE99]/25 blur-2xl lg:size-24'
                />

                <button
                    type='button'
                    onClick={openSidebar}
                    onMouseEnter={prefetchSidebarData}
                    onTouchStart={prefetchSidebarData}
                    tabIndex={hidden ? -1 : 0}
                    className='group relative z-[1] flex min-w-0 flex-1 items-center gap-2 py-2 ps-2.5 text-start lg:gap-3.5 lg:py-2.5 lg:ps-4'
                >
                    <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] ring-1 ring-[#42BE99]/30 lg:size-11 lg:rounded-xl'>
                        <TetherCoin3D className='size-5 drop-shadow-[0_2px_8px_rgba(66,190,153,0.45)] lg:size-7' />
                    </span>
                    <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                        <span className='text-[11px] font-bold text-[#7ee0c2] max-lg:hidden'>
                            اپلیکیشن تتر چند
                        </span>
                        {/* One line on phones: smaller text there, ellipsis only as a last resort on very narrow screens. */}
                        <span className='truncate text-[11px] font-bold leading-snug min-[390px]:text-xs lg:line-clamp-2 lg:whitespace-normal lg:text-sm'>
                            تتر را با ارزان‌ترین قیمت ممکن خرید کنید.
                        </span>
                    </span>
                    <span className='relative flex shrink-0 items-center gap-1 overflow-hidden rounded-lg bg-[#42BE99] px-2.5 py-1.5 text-[11px] font-black min-[390px]:text-xs text-[#01001B] shadow-[0_6px_16px_-6px_rgba(66,190,153,0.8)] transition group-hover:brightness-110 lg:rounded-xl lg:px-4 lg:py-2'>
                        {/* Light sweep across the button every few seconds */}
                        <span
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-y-0 left-0 w-2/3 -translate-x-full skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/70 to-transparent animate-[tcb-shine_4.5s_ease-in-out_1.5s_infinite] motion-reduce:hidden'
                        />
                        <span className='relative'>مشاهده</span>
                        <svg
                            className='relative'
                            viewBox='0 0 24 24'
                            width='14'
                            height='14'
                            aria-hidden='true'
                        >
                            <path
                                d='M14.5 6l-6 6 6 6'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2.4'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    </span>
                </button>

                <button
                    type='button'
                    onClick={() => setClosed(true)}
                    tabIndex={hidden ? -1 : 0}
                    aria-label='بستن'
                    title='بستن'
                    className='relative z-[1] mx-0.5 flex size-8 shrink-0 items-center justify-center rounded-full text-white/45 transition hover:bg-white/10 hover:text-white lg:mx-2'
                >
                    <svg viewBox='0 0 24 24' width='16' height='16' aria-hidden='true'>
                        <path
                            d='M6 6l12 12M18 6L6 18'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                        />
                    </svg>
                </button>
            </div>
            <style>
                {
                    '@keyframes tcb-shine{0%{transform:translateX(-120%) skewX(-20deg)}35%,100%{transform:translateX(260%) skewX(-20deg)}}'
                }
            </style>
        </aside>
    );
}
